import type { ImageOptions } from './imgix';

const PLACEHOLDER_GIF =
  'data:image/gif;base64,R0lGODlhAQABAID/AMDAwAAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

const CDAAS_ORIGIN_TO_SERVING_DOMAIN: Record<string, string> = {
  'cdaas-dev.americanexpress.com': 'static-dev.americanexpress.com',
  // qwww is the CDaaS E2/QA serving hostname.
  'cdaas-test.americanexpress.com': 'qwww.aexp-static.com',
  'cdaas.americanexpress.com': 'www.aexp-static.com',
};
const CDAAS_SERVING_DOMAINS = new Set(Object.values(CDAAS_ORIGIN_TO_SERVING_DOMAIN));

// Matches any CDaaS path: /gcs/<tock-public-assets-bucket>/<optional-directives>/<asset-path>
const CDAAS_DIRECTIVES_PATH = /^(\/gcs\/tock-public-assets-e[123])\/([^/]*_[^/]*)\/(.+)$/;
const CDAAS_BARE_PATH = /^(\/gcs\/tock-public-assets-e[123])\/(.+)$/;

interface CdaasUrlParts {
  origin: string;
  bucketPrefix: string;
  assetPath: string;
}

interface DirectiveOptions {
  width?: number;
  height?: number;
  q?: number;
  centerCrop?: boolean;
}

export function hasCdaasUrl(url?: string): boolean {
  if (!url) {
    return false;
  }
  return parseCdaasUrl(url) !== null;
}

function parseCdaasUrl(url: string): CdaasUrlParts | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const servingDomainFromOrigin = CDAAS_ORIGIN_TO_SERVING_DOMAIN[parsed.host];
  const servingDomain =
    servingDomainFromOrigin ?? (CDAAS_SERVING_DOMAINS.has(parsed.host) ? parsed.host : null);
  if (servingDomain === null) {
    return null;
  }

  const isOriginUrl = servingDomainFromOrigin !== undefined;
  const withDirectives = isOriginUrl ? null : parsed.pathname.match(CDAAS_DIRECTIVES_PATH);
  if (withDirectives) {
    return {
      origin: `https://${servingDomain}`,
      bucketPrefix: withDirectives[1],
      assetPath: withDirectives[3],
    };
  }
  const bare = parsed.pathname.match(CDAAS_BARE_PATH);
  if (bare) {
    return {
      origin: `https://${servingDomain}`,
      bucketPrefix: bare[1],
      assetPath: bare[2],
    };
  }
  return null;
}

function extractExistingDirectives(url: string): DirectiveOptions {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return {};
  }
  if (!CDAAS_SERVING_DOMAINS.has(parsed.host)) {
    return {};
  }
  const match = parsed.pathname.match(CDAAS_DIRECTIVES_PATH);
  if (!match) {
    return {};
  }
  const result: DirectiveOptions = {};
  match[2].split(',').forEach((part) => {
    if (part === 'c_fit') {
      result.centerCrop = true;
      return;
    }
    const [key, value] = part.split('_');
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return;
    }
    if (key === 'w') {
      result.width = numericValue;
    } else if (key === 'h') {
      result.height = numericValue;
    } else if (key === 'q') {
      result.q = numericValue;
    }
  });
  return result;
}

function buildDirectiveString(opts: DirectiveOptions): string {
  const parts: string[] = [];
  if (opts.centerCrop) {
    parts.push('c_fit');
  }
  if (opts.width !== undefined) {
    parts.push(`w_${opts.width}`);
  }
  if (opts.height !== undefined) {
    parts.push(`h_${opts.height}`);
  }
  if (opts.q !== undefined) {
    parts.push(`q_${opts.q}`);
  }
  return parts.join(',');
}

function buildUrl(parts: CdaasUrlParts, directives: string): string {
  const cleanPath = parts.assetPath.startsWith('/')
    ? parts.assetPath.substring(1)
    : parts.assetPath;
  let url = `${parts.origin}${parts.bucketPrefix}/`;
  if (directives) {
    url += `${directives}/`;
  }
  url += cleanPath;
  return url;
}

export function getAutoFormatUrl(url?: string, options?: ImageOptions): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  }
  const parts = parseCdaasUrl(url);
  if (parts === null) {
    return url;
  }
  const existing = extractExistingDirectives(url);
  const directives = buildDirectiveString({
    centerCrop: existing.centerCrop,
    width: options?.width ?? existing.width,
    height: options?.height ?? existing.height,
    q: options?.q ?? existing.q,
  });
  return buildUrl(parts, directives);
}

export function centerCropUrl(url?: string, options?: ImageOptions): string {
  if (!url) {
    return PLACEHOLDER_GIF;
  }
  const parts = parseCdaasUrl(url);
  if (parts === null) {
    return url;
  }
  const existing = extractExistingDirectives(url);
  const directives = buildDirectiveString({
    centerCrop: true,
    width: options?.width ?? existing.width,
    height: options?.height ?? existing.height,
    q: options?.q ?? existing.q,
  });
  return buildUrl(parts, directives);
}

export function centerCropThumbnailUrl(url?: string): string {
  return centerCropUrl(url, { width: 576, height: 384, q: 35 });
}
